import { Component, Input, OnChanges, OnDestroy, OnInit, Renderer2, SimpleChanges, TemplateRef, ViewChild, ViewEncapsulation } from '@angular/core';
import { Complaint, ComplaintAppointment, ComplaintData, ComplaintPriorities, complaintPriorities } from '@icc/b2b/shared';
import { ConfigService, TranslateService } from '@icc/shared';
import { ComplaintService } from '../complaint.service';
import { Router } from '@angular/router';
import { GoogleMap, MapGeocoder } from '@angular/google-maps';
import { GoogleMapsLoaderService } from '../google-maps-loader.service';
import { MarkerClusterer } from '@googlemaps/markerclusterer';
import { NgxPermissionsService } from 'ngx-permissions';
import { Subscription } from 'rxjs';
import { InputComponent } from '@icc/ui';
import { LoaderService, SyncService } from '@icc/b2b/sales-document';
import { IDBServiceBrowser } from 'libs/b2b/sales-document/src/lib/idb.service.browser';
import { OverlappingMarkerSpiderfier } from 'ts-overlapping-marker-spiderfier';

@Component({
  selector: 'icc-complaint-map',
  templateUrl: './complaint-map.component.html',
  styleUrls: ['./complaint-map.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class ComplaintMapComponent implements OnInit, OnChanges, OnDestroy {
    data: ComplaintData = {} as ComplaintData;

    priorityMap: Record<string, string> = {};

    appointments: ComplaintAppointment[] = [];

    editPriority = false;
    hasAccessTo = {
        manageComplaint: false,
    };
    documentPriority: string | null = null;

    private subscriptions: Subscription[] = [];

    apiLoaded = false;
    mapZoom: google.maps.MapOptions['zoom'] = 7;

    mapCenter: google.maps.LatLngLiteral = {
        lat: 52.18799140660359,
        lng: 19.22466337395079,
    };

    complaintsWithCoords: { complaint: Complaint, location: google.maps.LatLng }[] = [];

    @ViewChild('complaintsMap', { static: false }) complaintsMap: GoogleMap;
    @ViewChild('infoWindowTemplate', { static: true }) infoWindowTemplate: TemplateRef<any>;

    @ViewChild('documentPriority', { static: false }) priority: InputComponent;

    markers: google.maps.Marker[] = [];
    markerCluster: MarkerClusterer | null = null;

    @Input() filteredDocuments: Complaint[] = [];
    @Input() resetMapZoomAndCenter: boolean;

    documentPriorities = complaintPriorities;

    priorityGroup: string;
    priorityValue: number;

    constructor(
        private router: Router,
        private complaintService: ComplaintService,
        private geocoder: MapGeocoder,
        private renderer: Renderer2,
        private translateService: TranslateService,
        private permissionsService: NgxPermissionsService,
        private loaderService: LoaderService,
        private idb: IDBServiceBrowser,
        private configService: ConfigService,
        private syncService: SyncService,
        private googleMapsLoaderService: GoogleMapsLoaderService,
    ) {
        this.subscriptions.push(
            this.permissionsService.permissions$.subscribe(() => {
                this.checkAccess();
            }),
        );
        this.getAppointments();
    }

    async ngOnInit() {
        await this.loadData();
        try {
            await this.googleMapsLoaderService.load(this.data.googleMapsApiKey, ['marker']);
            this.apiLoaded = true;
            if (!this.resetMapZoomAndCenter) {
                this.setDefaultView();
            }
            this.getComplaintsWithCoords();
        } catch (error) {
            console.error('Failed to load Google Maps API:', error);
        }
    }

    ngOnChanges(changes: SimpleChanges): void {
        if(this.apiLoaded && changes.filteredDocuments) {
            this.getComplaintsWithCoords();
        }
    }

    ngOnDestroy() {
        this.subscriptions.forEach(subscription => subscription.unsubscribe());
    }

    async loadData() {
        await this.complaintService.loaded;
        this.data = this.complaintService.data;
    }

    setDefaultView() {
        const storedCenter = sessionStorage.getItem(`complaints-map-center-${
            this.configService?.user?.user?.id
        }`);
        const storedZoom = sessionStorage.getItem(`complaints-map-zoom-${
            this.configService?.user?.user?.id
        }`);

        if (storedCenter) {
            this.mapCenter = JSON.parse(storedCenter);
        }

        if (storedZoom) {
            this.mapZoom = JSON.parse(storedZoom);
        }
    }

    async getComplaintsWithCoords() {
        const addressPromises = this.filteredDocuments
            .filter((complaint) => complaint.installationAddress)
            .map(async (complaint) => {
                try {
                    const result = await this.geocoder.geocode({ address: complaint.installationAddress }).toPromise();
                    this.priorityMap[complaint.uuid] = this.complaintService.getPriority(complaint);
                    if (result?.results.length > 0) {
                        return { complaint, location: result.results[0].geometry.location };
                    } else {
                        console.error('Geocode was not successful for the following reason: No results found');
                        return null;
                    }
                } catch (error) {
                    console.error('Geocode was not successful for the following reason:', error);
                    return null;
                }
            });

            const locations = await Promise.all(addressPromises);
            this.complaintsWithCoords = locations.filter((item) => item !== null) as { complaint: Complaint, location: google.maps.LatLng }[];
            this.updateMapMarkers();
    }

    buildContent(complaint: Complaint): HTMLElement {
        const content = this.renderer.createElement('div');

        const appointmentCount = this.countFutureAppointmentsForComplaint(complaint.uuid);
        const closestAppointmentDetails = this.findClosestAppointmentScheduledTimeAndDate(complaint.uuid);

        const embeddedView = this.infoWindowTemplate.createEmbeddedView({
            data: complaint,
            priority: this.priorityMap[complaint.uuid],
            appointmentCount,
            closestAppointmentScheduledTime: closestAppointmentDetails?.closestAppointmentScheduledTime,
            closestAppointmentDate: closestAppointmentDetails?.closestAppointmentDate,
        });
        this.renderer.appendChild(content, embeddedView.rootNodes[0]);
        embeddedView.detectChanges();

        return content;
    }

    updateMapMarkers() {
        if (!this.complaintsMap || !this.apiLoaded) {
            return;
        }
        const map = this.complaintsMap.googleMap;

        const oms = new OverlappingMarkerSpiderfier(map, {
            markersWontMove: true,
            markersWontHide: true,
            basicFormatEvents: true,
            keepSpiderfied: true,
            legWeight: 2.5,
            circleFootSeparation: 50,
        });

        if (this.markerCluster) {
            this.markerCluster.clearMarkers();
        }

        this.markers.forEach(marker => marker.setMap(null));
        this.markers = [];

        this.markers = this.complaintsWithCoords.map(({ complaint, location }) => {
            const infoWindow = new google.maps.InfoWindow({
                content: this.buildContent(complaint),
                ariaLabel: 'Complaint',
                headerContent: this.buildHeader(),
            });

            const marker = new google.maps.Marker({
                position: location,
                map,
            });

            google.maps.event.addListener(marker, 'spider_click', () => {
                infoWindow.open({
                    anchor: marker,
                    map,
                });
            });

            oms.addMarker(marker, () => {
                infoWindow.open({
                    anchor: marker,
                    map,
                });
            });

            return marker;
        });

        this.markerCluster = new MarkerClusterer({
            map,
            markers: this.markers,
        });
    }

    open(document: Complaint) {
        this.complaintService.openDocument(document);
        this.router.navigate(['trade', 'complaint', document.uuid], { queryParams: { origin: 'map' } });
    }

    getPriority(document: Complaint) {
        const rawPriority = this.complaintService.getPriority(document);
            if (rawPriority) {
                const priorityGroup = ComplaintPriorities[+rawPriority.slice(0, 1)];
                const priorityValue = rawPriority.slice(1);
                return this.translateService.instant(priorityGroup) + '-' + priorityValue;
            }
        return '';
    }

    buildHeader(): HTMLElement {
        const infoWindowHeader = document.createElement('span');
        infoWindowHeader.className = 'complaint-info__header';
        infoWindowHeader.innerHTML = this.translateService.instant('COMPLAINT|Szczegóły reklamacji');

        return infoWindowHeader;
    }

    async changePriority(document: Complaint, priorityGroup: string, priorityValue: string) {
        this.syncService.setCurrentDocumentsIds([document.uuid]);
        await this.syncService.loadDocuments([document.uuid]);
        const paddedPriorityValue = priorityValue.padStart(2, '0');
        const priority = priorityGroup ? (priorityGroup + paddedPriorityValue) : (this.priorityMap[document.uuid].slice(0, 1) + paddedPriorityValue);
        this.complaintService.changePriority(document, priority);

        this.priorityMap[document.uuid] = priority;

        this.cancelEditPriority(document.uuid);
        this.updateMapMarkers();
    }

    async checkAccess() {
        this.hasAccessTo.manageComplaint = await this.permissionsService.hasPermission('complaints-manage');
    }

    async getAppointments() {
        this.loaderService.setLoadingAfterTime();
        const allAppointments = await this.idb.getAllDb('appointments');
        this.appointments = allAppointments;
        this.loaderService.setLoaded();
    }

    countFutureAppointmentsForComplaint(documentId: string): number {
        const now = new Date();
        now.setDate(now.getDate() - 1);
        return this.appointments.filter(appointment =>
            appointment.documentId === documentId &&
            !appointment.isDeleted &&
            new Date(appointment.date).getTime() > now.getTime() &&
            (appointment.status !== 'completed-pending' && appointment.status !== 'completed' && appointment.status !== 'rejected' && appointment.status !== 'confirmed')
        ).length;
    }

    findClosestAppointmentScheduledTimeAndDate(documentId: string) {
        const now = new Date();
        now.setDate(now.getDate() - 1);
        const futureAppointmentsForDocument = this.appointments.filter(appointment =>
            appointment.documentId === documentId &&
            !appointment.isDeleted &&
            new Date(appointment.date).getTime() > now.getTime() &&
            (appointment.status !== 'completed-pending' && appointment.status !== 'completed' && appointment.status !== 'rejected' && appointment.status !== 'confirmed')
        );

        if (futureAppointmentsForDocument.length === 0) {
            return null;
        }

        const closestAppointment = futureAppointmentsForDocument.reduce((closest, current) => {
            const closestDateDiff = Math.abs(new Date(closest.date).getTime() - now.getTime());
            const currentDateDiff = Math.abs(new Date(current.date).getTime() - now.getTime());
            return currentDateDiff < closestDateDiff ? current : closest;
        });

        return { closestAppointmentScheduledTime: closestAppointment.scheduledTime, closestAppointmentDate: closestAppointment.date } || null;
    }

    showEditMode(complaintUuid: string) {
        (document.querySelector('#priorityValue' + complaintUuid) as HTMLElement).style.display = 'none';
        (document.querySelector('#editButton' + complaintUuid) as HTMLElement).style.display = 'none';

        (document.querySelector('#priorityEdit' + complaintUuid) as HTMLElement).style.display = 'flex';
        (document.querySelector('#priorityEdit' + complaintUuid) as HTMLElement).style.alignItems = 'center';

        if (!(document.querySelector('#priorityInput' + complaintUuid) as HTMLInputElement).value) {
            (document.querySelector('#saveButton' + complaintUuid) as HTMLButtonElement).disabled = true;
        }

        (document.querySelector('#priorityInput' + complaintUuid) as HTMLInputElement).addEventListener('input', () => {
            const isValid = (document.querySelector('#priorityInput' + complaintUuid) as HTMLInputElement).value &&
                            (document.querySelector('#priorityInput' + complaintUuid) as HTMLInputElement).value.length <= 2 &&
                            +(document.querySelector('#priorityInput' + complaintUuid) as HTMLInputElement).value >= 1 &&
                            +(document.querySelector('#priorityInput' + complaintUuid) as HTMLInputElement).value <= 99;
            (document.querySelector('#saveButton' + complaintUuid) as HTMLButtonElement).disabled = !isValid;
            (document.querySelector('#saveButton' + complaintUuid) as HTMLButtonElement).style.cursor = !isValid ? 'default' : 'pointer';
          });
    }

    cancelEditPriority(complaintUuid: string) {
        (document.querySelector('#priorityEdit' + complaintUuid) as HTMLElement).style.display = 'none';
        (document.querySelector('#priorityValue' + complaintUuid) as HTMLElement).style.display = 'flex';
        (document.querySelector('#priorityValue' + complaintUuid) as HTMLElement).style.alignItems = 'center';
        (document.querySelector('#editButton' + complaintUuid) as HTMLElement).style.display = 'flex';
    }

    onCenterChanged() {
       const mapCenter = this.complaintsMap?.getCenter()?.toJSON();
       sessionStorage.setItem(`complaints-map-center-${
            this.configService?.user?.user?.id
        }`, JSON.stringify(mapCenter));
    }

    onZoomChanged() {
        const mapZoom = this.complaintsMap?.getZoom();
        sessionStorage.setItem(`complaints-map-zoom-${
            this.configService?.user?.user?.id
        }`, JSON.stringify(mapZoom));
    }
}
